import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';
import { FilterAlt as FilterIcon, Clear as ClearIcon } from '@mui/icons-material';

const FilterPopover = ({ columns = [], handleFilterChange, clearAllFilters, filterConfig, uniqueValues }) => {
    const theme = useTheme();
    const filterableColumns = columns.filter(col => col.filterType);

    const hasActiveFilters = Object.keys(filterConfig).some(
        key => filterConfig[key]?.value !== undefined &&
            filterConfig[key]?.value !== '' &&
            filterConfig[key]?.value !== false &&
            (!Array.isArray(filterConfig[key]?.value) || filterConfig[key]?.value.length > 0
            )
    );

    return (
        <Box sx={{
            p: 1,
            width: 300,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[3],
            backgroundColor: theme.palette.background.paper
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Filters</Typography>
                </Box>
                <Button
                    size="small"
                    onClick={clearAllFilters}
                    startIcon={<ClearIcon fontSize="small" />}
                    disabled={!hasActiveFilters}
                    sx={{
                        textTransform: 'none',
                        color: hasActiveFilters ? 'primary.main' : 'text.disabled',
                        '&:hover': {
                            color: hasActiveFilters ? 'primary.dark' : 'text.disabled',
                        }
                    }}
                >
                    Clear all
                </Button>
            </Box>

            {/* Filter Content */}
            {filterableColumns.length > 0 ? (
                <Box sx={{
                    maxHeight: 'calc(100vh - 200px)',
                    overflowY: 'auto',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                        width: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 3,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: theme.palette.action.selected,
                    }
                }}>
                    {filterableColumns.map(col => (
                        <Box key={col.key} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                {col.label}
                            </Typography>

                            {col.filterType === 'text' && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    value={filterConfig[col.key]?.value || ''}
                                    onChange={(e) => handleFilterChange(col.key, e.target.value, 'text')}
                                    placeholder={`Search ${col.label}`}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            fontSize: '0.875rem',
                                        }
                                    }}
                                />
                            )}

                            {col.filterType === 'date' && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    variant="outlined"
                                    value={filterConfig[col.key]?.value || ''}
                                    onChange={(e) => handleFilterChange(col.key, e.target.value, 'date')}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            fontSize: '0.875rem',
                                        }
                                    }}
                                />
                            )}

                            {col.filterType === 'boolean' && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={filterConfig[col.key]?.value || false}
                                            onChange={(e) => handleFilterChange(col.key, e.target.checked, 'boolean')}
                                            color="primary"
                                        />
                                    }
                                    label={filterConfig[col.key]?.value ? 'Enabled' : 'Disabled'}
                                    sx={{
                                        marginLeft: 0,
                                        '& .MuiTypography-root': {
                                            fontSize: '0.875rem',
                                        }
                                    }}
                                />
                            )}

                            {col.filterType === 'select' && (
                                <Autocomplete
                                    multiple
                                    options={uniqueValues[col.key] || []}
                                    value={filterConfig[col.key]?.value || []}
                                    onChange={(event, newValue) => {
                                        handleFilterChange(col.key, newValue, 'select');
                                    }}
                                    disableCloseOnSelect
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Select options..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1,
                                                    fontSize: '0.875rem',
                                                    minHeight: 40,
                                                    py: 0.5,
                                                }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                size="small"
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">{option}</Typography>
                                        </li>
                                    )}
                                    renderTags={(value, getTagProps) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {value.map((option, index) => (
                                                <Chip
                                                    key={option}
                                                    label={option}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    {...getTagProps({ index })}
                                                    sx={{
                                                        borderRadius: 1,
                                                        '& .MuiChip-deleteIcon': {
                                                            fontSize: '1rem',
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    componentsProps={{
                                        paper: {
                                            sx: {
                                                mt: 1,
                                                boxShadow: theme.shadows[2],
                                                '& .MuiAutocomplete-listbox': {
                                                    py: 0,
                                                    maxHeight: 200,
                                                }
                                            }
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{
                    py: 2,
                    textAlign: 'center',
                    fontStyle: 'italic'
                }}>
                    No filterable columns available
                </Typography>
            )}
        </Box>
    );
};

export default FilterPopover;